import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Moon, TrendingUp, TrendingDown, CheckCircle, AlertCircle, Flame } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EndOfDaySummary = ({ token }) => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/recommendations/end-of-day-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
        <div className="text-center text-gray-500 dark:text-gray-400">{t('endOfDay.loadingSummary')}</div>
      </div>
    );
  }

  if (!summary) return null;

  const StatusIcon = summary.status === 'perfect' ? CheckCircle :
                     summary.status === 'over' ? AlertCircle : TrendingDown;
  
  const statusColor = summary.status === 'perfect' ? 'text-green-500' :
                      summary.status === 'over' ? 'text-red-500' : 'text-yellow-500';

  const GoalIcon = summary.goal === 'lose weight' ? TrendingDown :
                   summary.goal === 'gain weight' ? TrendingUp : CheckCircle;

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-testid="end-of-day-summary">
      <div className="flex items-center gap-3 mb-4">
        <Moon size={24} className="text-[#FF6600]" />
        <div>
          <h2 className="text-lg font-bold">{t('endOfDay.title')}</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">{summary.date}</p>
        </div>
      </div>

      {/* Status Message */}
      <div className={`p-4 rounded-xl mb-4 border-2 ${
        summary.status === 'perfect' ? 'bg-green-50 border-green-500' :
        summary.status === 'over' ? 'bg-red-50 border-red-500' : 'bg-yellow-50 border-yellow-500'
      }`}>
        <div className="flex items-start gap-4">
          <StatusIcon size={24} className={statusColor} />
          <div className="flex-1">
            <h3 className="font-bold mb-2">{summary.message}</h3>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('endOfDay.consumed')}</div>
                <div className="font-bold">{Math.round(summary.consumed_calories)} cal</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('endOfDay.target')}</div>
                <div className="font-bold">{Math.round(summary.target_calories)} cal</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center p-4 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
          <div className="font-bold text-[#FF6600]">{summary.meals_logged}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Meals Logged</div>
        </div>
        <div className="text-center p-4 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
          <div className="font-bold text-[#FF6600] flex items-center justify-center gap-1">
            <Flame size={16} />
            {summary.streak}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Day Streak</div>
        </div>
        <div className="text-center p-4 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
          <div className="font-bold text-[#FF6600] flex items-center justify-center gap-1">
            <GoalIcon size={16} />
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{summary.goal}</div>
        </div>
      </div>

      {/* Tomorrow's Tip */}
      <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-[#FF6600]">
        <div className="font-semibold text-sm mb-2">💡 Tomorrow's Strategy</div>
        <p className="text-sm text-gray-700 dark:text-gray-300">{summary.tomorrow_tip}</p>
      </div>

      {/* Motivational Message */}
      <div className="mt-4 text-center">
        {summary.status === 'perfect' && (
          <p className="text-sm text-green-600">
            🎉 Perfect day! Keep up the excellent work!
          </p>
        )}
        {summary.status === 'over' && summary.goal === 'lose weight' && (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Tomorrow is a new day. You've got this! 💪
          </p>
        )}
        {summary.status === 'under' && summary.goal === 'gain weight' && (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Great job! You're on track to reach your goals! 🚀
          </p>
        )}
        {summary.status === 'under' && summary.goal === 'lose weight' && (
          <p className="text-sm text-green-600">
            Excellent work staying in your calorie deficit! 🎯
          </p>
        )}
      </div>
    </div>
  );
};

export default EndOfDaySummary;
